import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LogOut, Users, UserCheck, CreditCard, Euro, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, formatDate } from "@/lib/api";
import { Logo } from "@/components/site/Logo";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, WaitlistTable } from "@/components/admin/AdminTables";
import { AdminLogin } from "@/components/admin/AdminLogin";
import { AdminIntegrations } from "@/components/admin/AdminIntegrations";
import { AdminDiagnostics } from "@/components/admin/AdminDiagnostics";
import { useAuth } from "@/context/AuthContext";
import { usePageTitle } from "@/hooks/useMimis";

const KEY = "mimis_admin_token";

const Stat = ({ icon: Icon, label, value, testId }) => (
  <div className="card-soft p-6 hover:translate-y-0" data-testid={testId}>
    <div className="flex items-center justify-between text-sm text-[#52525b]">{label}<Icon className="h-4 w-4 text-[#0066FF]" /></div>
    <p className="font-display mt-3 text-4xl font-extrabold tracking-tight">{value}</p>
  </div>
);

const STATUS = { paid: "Pagato", pending: "In attesa", expired: "Scaduto", failed: "Fallito", refunded: "Rimborsato" };

const Dashboard = ({ data, headers, reload, onLogout }) => {
  const s = data?.stats ?? {};

  const addCredits = async (u, n) => {
    try {
      await api.post(`/admin/users/${u.user_id}/crediti`, { quantita: n }, { headers });
      toast.success(`${n > 0 ? "+" : ""}${n} crediti a ${u.email}`);
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`Eliminare ${r.nome} dalla lista d'attesa?`)) return;
    try {
      await api.delete(`/admin/waitlist/${r.id}`, { headers });
      toast.success("Iscritto eliminato");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <main className="min-h-screen bg-[#F5F5F7]" data-testid="admin-dashboard">
      <header className="sticky top-0 z-40 border-b border-[#e7e7ea] bg-white/85 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 sm:px-8">
          <Link to="/" className="flex items-center gap-3"><Logo /><span className="hidden rounded-full bg-[#111111] px-2.5 py-1 text-xs font-semibold text-white sm:inline">Admin</span></Link>
          <div className="flex items-center gap-2">
            <button onClick={reload} className="btn-ghost h-10 px-4 text-sm" data-testid="admin-refresh-button"><RefreshCw className="h-4 w-4" /><span className="hidden sm:inline">Aggiorna</span></button>
            <button onClick={onLogout} className="btn-ghost h-10 px-4 text-sm" data-testid="admin-logout-button"><LogOut className="h-4 w-4" /><span className="hidden sm:inline">Esci</span></button>
          </div>
        </div>
      </header>
      <div className="mx-auto max-w-7xl space-y-8 px-5 py-10 sm:px-8">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">Ciao Franco</h1>
          <p className="mt-1 text-[#52525b]">Ecco come sta crescendo MIMIS.</p>
        </div>
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <Stat icon={Users} label="Iscritti in lista" value={s.iscritti ?? 0} testId="stat-waitlist" />
          <Stat icon={UserCheck} label="Utenti Google" value={s.utenti ?? 0} testId="stat-users" />
          <Stat icon={CreditCard} label="Abbonati" value={s.abbonati ?? 0} testId="stat-subscribers" />
          <Stat icon={Euro} label="Incasso mensile" value={`${(s.mrr ?? 0).toLocaleString("it-IT")}€`} testId="stat-mrr" />
        </div>
        <div className="card-soft flex flex-col gap-2 p-6 hover:translate-y-0 sm:flex-row sm:items-center sm:justify-between" data-testid="plans-card">
          <p className="font-display text-lg font-bold">Abbonati per piano</p>
          <p className="text-sm text-[#52525b]">Starter 9€ (10 video/mese): <b data-testid="plans-starter-count">{s.starter ?? 0}</b> · Pro 19€ (illimitati): <b data-testid="plans-pro-count">{s.pro ?? 0}</b></p>
        </div>
        <AdminIntegrations headers={headers} />
        <AdminDiagnostics headers={headers} />
        <Tabs defaultValue="lista">
          <TabsList className="h-auto flex-wrap rounded-full bg-white p-1">
            <TabsTrigger value="lista" className="rounded-full px-4 py-2" data-testid="admin-tab-waitlist">Lista d'attesa ({s.iscritti ?? 0})</TabsTrigger>
            <TabsTrigger value="pagamenti" className="rounded-full px-4 py-2" data-testid="admin-tab-payments">Pagamenti</TabsTrigger>
            <TabsTrigger value="utenti" className="rounded-full px-4 py-2" data-testid="admin-tab-users">Utenti</TabsTrigger>
            <TabsTrigger value="video" className="rounded-full px-4 py-2" data-testid="admin-tab-videos">Video ({s.video ?? 0})</TabsTrigger>
          </TabsList>
          <TabsContent value="lista" className="mt-5"><WaitlistTable rows={data?.waitlist} onDelete={remove} /></TabsContent>
          <TabsContent value="pagamenti" className="mt-5">
            <Table testId="admin-payments-table" cols={["Cliente", "Email", "Piano", "Importo", "Stato", "Data"]} rows={data?.payments ?? []} empty="Nessun pagamento ancora."
              render={(p) => (
                <tr key={p.session_id} className="border-t border-[#f0f0f2]">
                  <td className="px-4 py-3 font-medium">{p.nome || "—"}</td>
                  <td className="px-4 py-3">{p.email}</td>
                  <td className="px-4 py-3">{p.piano === "starter" ? "Starter 9€" : p.piano === "pro" ? "Pro 19€" : p.piano === "fondatori" ? "Fondatori 9€" : "Mensile 19€"}</td>
                  <td className="px-4 py-3">{p.amount}€</td>
                  <td className="px-4 py-3"><span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${p.payment_status === "paid" && p.subscription_status !== "canceled" ? "bg-emerald-50 text-emerald-700" : "bg-[#F5F5F7] text-[#52525b]"}`}>{p.subscription_status === "canceled" ? "Disdetto" : p.cancel_at_period_end ? "In disdetta" : STATUS[p.payment_status] || p.payment_status}</span></td>
                  <td className="px-4 py-3 text-[#52525b]">{formatDate(p.created_at)}</td>
                </tr>
              )} />
          </TabsContent>
          <TabsContent value="utenti" className="mt-5">
            <Table testId="admin-users-table" cols={["Nome", "Email", "Registrato il", "Crediti bonus"]} rows={data?.users ?? []} empty="Nessun utente registrato."
              render={(u) => (
                <tr key={u.email} className="border-t border-[#f0f0f2]">
                  <td className="px-4 py-3 font-medium">{u.name || "—"}</td>
                  <td className="px-4 py-3">{u.email}</td>
                  <td className="px-4 py-3 text-[#52525b]">{formatDate(u.created_at)}</td>
                  <td className="whitespace-nowrap px-4 py-3">
                    <span className="mr-3 font-semibold" data-testid={`credits-${u.user_id}`}>{u.crediti_bonus ?? 0}</span>
                    <button onClick={() => addCredits(u, 5)} className="rounded-full bg-[#0066FF]/10 px-3 py-1 text-xs font-semibold text-[#0066FF] hover:bg-[#0066FF]/20" data-testid={`add-credits-${u.user_id}`}>+5</button>
                    <button onClick={() => addCredits(u, -1)} className="ml-1 rounded-full bg-[#F5F5F7] px-3 py-1 text-xs font-semibold text-[#52525b] hover:bg-[#ececef]" data-testid={`remove-credit-${u.user_id}`}>-1</button>
                  </td>
                </tr>
              )} />
          </TabsContent>
          <TabsContent value="video" className="mt-5">
            <Table testId="admin-videos-table" cols={["Video", "Utente", "Voce", "Stato", "Data"]} rows={data?.videos ?? []} empty="Nessun video creato."
              render={(v) => (
                <tr key={v.id} className="border-t border-[#f0f0f2]">
                  <td className="px-4 py-3 font-medium">{v.titolo || v.idea}</td>
                  <td className="px-4 py-3">{v.email || "—"}</td>
                  <td className="px-4 py-3 capitalize">{v.voce}{v.voce_provider ? ` · ${v.voce_provider === "elevenlabs" ? "ElevenLabs" : "riserva"}` : ""}</td>
                  <td className="px-4 py-3">{{ pronto: "Pronto", errore: "Errore", in_corso: "In corso", in_coda: "In coda" }[v.stato] || v.stato}{v.errore_tecnico && <span className="mt-1 block max-w-xs break-words text-xs text-red-700">{v.errore_tecnico}</span>}</td>
                  <td className="px-4 py-3 text-[#52525b]">{formatDate(v.created_at)}</td>
                </tr>
              )} />
          </TabsContent>
        </Tabs>
      </div>
    </main>
  );
};

export default function Admin() {
  const [token, setToken] = useState(() => sessionStorage.getItem(KEY));
  const [data, setData] = useState(null);
  const [denied, setDenied] = useState(false);
  const { user, loading } = useAuth();
  usePageTitle("Admin | MIMIS", true);
  const headers = token ? { "X-Admin-Token": token } : {};

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get("/admin/overview", { headers: token ? { "X-Admin-Token": token } : {} });
      setData(d);
      setDenied(false);
    } catch {
      sessionStorage.removeItem(KEY);
      setDenied(true);
    }
  }, [token]);

  useEffect(() => {
    if (loading) return;
    if (token || user?.is_admin) load();
    else setDenied(true);
  }, [token, user, loading, load]);

  const saveToken = (t) => {
    sessionStorage.setItem(KEY, t);
    setToken(t);
  };
  const logout = () => {
    sessionStorage.removeItem(KEY);
    setToken(null);
    setData(null);
    setDenied(true);
  };

  if (denied && !data) return <AdminLogin onToken={saveToken} />;
  if (!data) return <main className="grid min-h-screen place-items-center"><Logo className="animate-pulse" /></main>;
  return <Dashboard data={data} headers={headers} reload={load} onLogout={logout} />;
}
